import {
  CustomConditionModel,
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
  SpecialConditionDataConditionType,
} from '@urbanx/agx.campaigns.client';
import dayjs from 'dayjs';

type SpecialConditionDueDateUpdate = FormCondition | string | undefined;

const toFormConditionDueDate = (
  newDueDate: SpecialConditionDueDateUpdate
): FormCondition | undefined => {
  if (!newDueDate) return undefined;

  if (typeof newDueDate === 'string') {
    return {
      referenceDate: newDueDate,
    };
  }

  return newDueDate;
};

const withUpdatedCustomConditionDueDate = (
  customCondition: CustomConditionModel,
  clauseId: string,
  newDueDate: SpecialConditionDueDateUpdate
): CustomConditionModel => {
  if (customCondition.clauseId === clauseId) {
    return {
      ...customCondition,
      overriddenDueDate: toFormConditionDueDate(newDueDate),
    };
  }

  return {
    ...customCondition,
    subClauses: customCondition.subClauses?.map((subClause) =>
      withUpdatedCustomConditionDueDate(subClause, clauseId, newDueDate)
    ),
  };
};

export const withUpdatedSpecialConditionDueDate = (
  condition: SpecialConditionData,
  clauseId: string,
  newDueDate: SpecialConditionDueDateUpdate
): SpecialConditionData => {
  if (condition.conditionType === SpecialConditionDataConditionType.Custom) {
    if (condition.id === clauseId && condition.customCondition) {
      return {
        ...condition,
        customCondition: {
          ...condition.customCondition,
          overriddenDueDate: toFormConditionDueDate(newDueDate),
        },
      };
    }

    return {
      ...condition,
      customCondition: condition.customCondition
        ? withUpdatedCustomConditionDueDate(condition.customCondition, clauseId, newDueDate)
        : undefined,
    };
  }

  return {
    ...condition,
    clauses: condition.clauses?.map((clause) => {
      if (clause.clauseId !== clauseId) return clause;

      return {
        ...clause,
        overriddenDueDate: toFormConditionDueDate(newDueDate),
      };
    }),
  };
};

const findCustomConditionDueDate = (
  customCondition: CustomConditionModel | undefined,
  clauseId: string
): FormCondition | undefined => {
  if (!customCondition) return undefined;

  if (customCondition.clauseId === clauseId) {
    return customCondition.overriddenDueDate;
  }

  for (const subClause of customCondition.subClauses ?? []) {
    const dueDate = findCustomConditionDueDate(subClause, clauseId);

    if (dueDate) return dueDate;
  }

  return undefined;
};

export const findSpecialConditionDueDate = (
  condition: SpecialConditionData | undefined,
  clauseId: string | undefined
): FormCondition | undefined => {
  if (!condition || !clauseId) return undefined;

  if (condition.conditionType === SpecialConditionDataConditionType.Custom) {
    if (condition.id === clauseId) return condition.customCondition?.overriddenDueDate;

    return findCustomConditionDueDate(condition.customCondition, clauseId);
  }

  return condition.clauses?.find((clause) => clause.clauseId === clauseId)?.overriddenDueDate;
};

export const getEarliestSpecialConditionBadgeDueDate = (
  renderedSpecialCondition: RenderedSpecialCondition
): string | undefined => {
  let earliestDueDate: string | undefined;
  let earliestParsedDueDate: dayjs.Dayjs | undefined;

  const dueDates = [
    renderedSpecialCondition.directDueDate,
    ...(renderedSpecialCondition.clauses?.map((clause) => clause.dueDate) ?? []),
  ];

  for (const dueDate of dueDates) {
    if (!isValidSpecialConditionDueDate(dueDate)) continue;

    const parsedDueDate = dayjs(dueDate);
    if (!earliestParsedDueDate || parsedDueDate.isBefore(earliestParsedDueDate)) {
      earliestDueDate = dueDate;
      earliestParsedDueDate = parsedDueDate;
    }
  }

  return earliestDueDate;
};

export const isValidSpecialConditionDueDate = (date: string | undefined): date is string =>
  !!date && dayjs(date).isValid();

export const isActionableSpecialCondition = (
  renderedSpecialCondition: RenderedSpecialCondition
): boolean =>
  isValidSpecialConditionDueDate(renderedSpecialCondition.directDueDate) ||
  !!renderedSpecialCondition.clauses?.some((clause) =>
    isValidSpecialConditionDueDate(clause.dueDate)
  );
